/**
 * Dialog: marcar candidato como contratado.
 *
 * Fluxo:
 * 1. Recrutador clica "Marcar como contratado" no CandidateModal
 * 2. Este dialog abre com:
 *    - Confirmação do nome do candidato e vaga
 *    - Campo opcional: salário negociado
 *    - Campo opcional: observações da contratação
 *    - Checkbox: "Encerrar a vaga automaticamente" (default ON)
 *    - Checkbox: "Enviar e-mail de agradecimento aos demais candidatos" (default ON)
 * 3. Submit → POST /api/candidates/{id}/hire
 * 4. Toast com resumo + reload do contexto
 */
import axios from "axios";
import { useState } from "react";
import { Loader2, Trophy, Mail, Lock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";

// Formats a string of raw digits (treated as cents) as "7.500,00".
const formatCentsAsBRL = (digits) => {
  if (!digits) return "";
  const cents = parseInt(digits, 10);
  return (cents / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const HireCandidateDialog = ({
  open,
  onClose,
  candidate,
  jobTitle = "",
  onHired,
}) => {
  // Salary is kept as raw digits (cents) internally — e.g. typing "7500"
  // stores "750000" and displays "7.500,00" — so the field always reflects
  // BRL formatting live as the user types, instead of a plain number input.
  const [salaryDigits, setSalaryDigits] = useState("");
  const [notes, setNotes] = useState("");
  const [closeJob, setCloseJob] = useState(true);
  const [sendThankYou, setSendThankYou] = useState(true);
  const [busy, setBusy] = useState(false);

  const handleSalaryChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 12);
    setSalaryDigits(digits);
  };

  const handleSubmit = async () => {
    if (!candidate?.id) return;
    if (!salaryDigits) {
      toast.error("Informe o salário negociado");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        hired_salary: parseInt(salaryDigits, 10) / 100,
        hire_notes: notes,
        close_job: closeJob,
        send_thank_you: sendThankYou,
      };
      const res = await axios.post(
        `${API}/candidates/${candidate.id}/hire`,
        payload,
      );
      toast.success(res.data.message, { duration: 6000 });
      if (onHired) onHired(res.data);
      onClose();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erro ao marcar como contratado",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!candidate) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent
        className="max-w-md"
        data-testid="hire-candidate-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shadow-md">
              <Trophy className="w-5 h-5" />
            </div>
            <div>
              <div className="text-lg font-bold text-slate-900">
                Marcar como contratado
              </div>
              <div className="text-xs font-normal text-slate-500">
                {candidate.name} {jobTitle && `· ${jobTitle}`}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="hire-salary" className="text-xs">
              Salário negociado
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500 pointer-events-none">
                R$
              </span>
              <Input
                id="hire-salary"
                type="text"
                inputMode="numeric"
                placeholder="0,00"
                value={formatCentsAsBRL(salaryDigits)}
                onChange={handleSalaryChange}
                required
                data-testid="hire-salary-input"
                className="text-sm pl-9"
              />
            </div>
            <p className="text-[11px] text-slate-500">
              Usado no relatório de fechamento (custo por contratação).
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="hire-notes" className="text-xs">
              Observações sobre a contratação (opcional)
            </Label>
            <Textarea
              id="hire-notes"
              rows={2}
              placeholder="Ex: Excelente fit técnico, ideal pra liderança..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="hire-notes-input"
              className="text-sm resize-none"
            />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={closeJob}
                onCheckedChange={(v) => setCloseJob(!!v)}
                data-testid="hire-close-job-checkbox"
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
                  <Lock className="w-3.5 h-3.5 text-amber-600" />
                  Encerrar a vaga automaticamente
                </div>
                <p className="text-[11px] text-slate-500">
                  Para todos os lembretes automáticos e alertas da Maya.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-2.5 cursor-pointer">
              <Checkbox
                checked={sendThankYou}
                onCheckedChange={(v) => setSendThankYou(!!v)}
                data-testid="hire-thank-you-checkbox"
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-900">
                  <Mail className="w-3.5 h-3.5 text-sky-600" />
                  Enviar agradecimento aos demais candidatos
                </div>
                <p className="text-[11px] text-slate-500">
                  E-mail cordial aos candidatos que ainda estavam no funil
                  (analisado, contatado, em entrevista, aprovado).
                </p>
              </div>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            data-testid="hire-cancel"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={busy || !salaryDigits}
            data-testid="hire-confirm"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
            )}
            Confirmar contratação
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HireCandidateDialog;
